import { useContext, useEffect, useRef } from 'react';
import noop from 'lodash/noop';
import Router from 'next/router';

import { getRedirectUrl, isAuthPage } from 'Components/RouterMiddleware/RouterMiddleware.helpers';
import { AppContext } from 'Context/AppContext';
import isClientSideAuth from 'Utilities/helpers/isClientSideAuth';

const AUTH_SYNC_INTERVAL_MS = 5000;

/**
 * Watches the access-token cookie and clears AppContext.user when the cookie
 * disappears (e.g. after a force-logout that invalidates tokens server-side).
 *
 * Clears the user from AppContext so the nav and route guards update, then
 * redirects to sign-in if the user is currently on a protected page.
 *
 * Only polls while an authenticated session exists. Once the user is cleared,
 * polling stops and the hook relies on visibility/focus events to re-check.
 */
export const useAuthSync = () => {
	const appContext = useContext(AppContext);
	const appContextRef = useRef(appContext);
	appContextRef.current = appContext;

	useEffect(() => {
		const intervalRef: { current?: ReturnType<typeof setInterval> } = {};

		const syncAuthState = () => {
			const ctx = appContextRef.current;
			const hasToken = isClientSideAuth();
			const hasUser = Boolean(ctx?.state.user);

			if (!hasToken && hasUser) {
				ctx?.update({ user: null });

				// If the user is on a protected page, redirect to sign-in
				const currentPath = Router.pathname;
				if (isAuthPage(currentPath)) {
					Router.push(getRedirectUrl(Router.asPath)).catch(noop);
				}
			}

			// Stop polling once there's nothing to watch for
			if (!hasToken && !hasUser && intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = undefined;
			}
		};

		// Check on visibility change and focus (covers tab-switch scenarios)
		document.addEventListener('visibilitychange', syncAuthState);
		globalThis.addEventListener('focus', syncAuthState);

		// Only poll while an authenticated session may exist
		if (appContextRef.current?.state.user || isClientSideAuth()) {
			intervalRef.current = setInterval(syncAuthState, AUTH_SYNC_INTERVAL_MS);
		}

		// Run immediately on mount to catch cases where the cookie is already gone
		syncAuthState();

		return () => {
			document.removeEventListener('visibilitychange', syncAuthState);
			globalThis.removeEventListener('focus', syncAuthState);
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = undefined;
			}
		};
	}, []);
};
